import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { formatCurrency, formatNumber } from '@/lib/format';
import { cn } from '@/lib/utils';

/**
 * Composition of a period in one bar — which tender types took the money, which
 * departments made the sales.
 *
 * The list underneath already ranks the rows and gives each one its share, so a
 * second ranked chart would repeat it. What no row can show is the whole: how
 * much of the period *this* slice is, next to the others, at a glance. That is
 * the job a 100% stacked bar does and a stack of meters does not.
 *
 * Four colours is the ceiling and it is a hard one — a fifth categorical hue
 * cannot be told from these four under colour-vision deficiency, so the tail
 * folds into a neutral "Other" rather than inventing one. Since the tail is
 * usually the long thin end of the distribution, that costs the reader nothing
 * and the table has every row anyway.
 */

const MAX_SERIES = 4;

/** Reads the validated slots from the stylesheet, so a slot means one thing everywhere. */
const SERIES_COLOR = [
  'var(--chart-1)',
  'var(--chart-2)',
  'var(--chart-3)',
  'var(--chart-4)',
];

/** The folded tail is deliberately not a hue — it is the absence of identity. */
const OTHER_COLOR = 'var(--muted-foreground)';

interface Slice {
  id: string;
  name: string;
  value: number;
  percent: number;
  color: string;
}

export function ShareBar<T>({
  rows,
  getId,
  getName,
  getValue,
  isMoney = true,
  /** Names the quantity being split, for the tooltip. */
  valueLabel,
  className,
}: {
  rows: T[];
  getId: (row: T) => string;
  getName: (row: T) => string;
  getValue: (row: T) => number;
  isMoney?: boolean;
  valueLabel: string;
  className?: string;
}) {
  const { t } = useTranslation();

  const slices = useMemo<Slice[]>(() => {
    const ranked = [...rows]
      .map((row) => ({
        id: getId(row),
        name: getName(row),
        value: getValue(row),
      }))
      // A negative row — a period of net refunds on a tender type — cannot be a
      // share of anything, so it is left out of the bar rather than drawn.
      .filter((row) => row.value > 0)
      .sort((a, b) => b.value - a.value);

    const total = ranked.reduce((sum, row) => sum + row.value, 0);
    if (total <= 0) return [];

    const head = ranked.slice(0, MAX_SERIES);
    const tail = ranked.slice(MAX_SERIES);
    const out: Slice[] = head.map((row, index) => ({
      ...row,
      percent: (row.value / total) * 100,
      color: SERIES_COLOR[index],
    }));

    if (tail.length > 0) {
      const tailValue = tail.reduce((sum, row) => sum + row.value, 0);
      out.push({
        id: '__other__',
        name: t('dashboard.otherCount', { count: tail.length }),
        value: tailValue,
        percent: (tailValue / total) * 100,
        color: OTHER_COLOR,
      });
    }
    return out;
  }, [rows, getId, getName, getValue, t]);

  if (slices.length === 0) return null;

  const format = (value: number) =>
    isMoney ? formatCurrency(value) : formatNumber(value);

  return (
    <div className={cn('space-y-2', className)}>
      {/* `gap-0.5` is the 2px surface gap that separates the segments. A stroke
          around each one would add ink that is not data. */}
      <div className="flex h-7 gap-0.5">
        {slices.map((slice) => (
          <Tooltip key={slice.id}>
            <TooltipTrigger asChild>
              <div
                className="h-full min-w-1 first:rounded-l-md last:rounded-r-md"
                style={{
                  width: `${slice.percent}%`,
                  backgroundColor: slice.color,
                }}
              />
            </TooltipTrigger>
            <TooltipContent>
              <p className="font-semibold">{slice.name}</p>
              <p>
                {slice.percent.toFixed(1)}% {t('dashboard.ofValue', { value: valueLabel.toLowerCase() })}
              </p>
              <p className="text-muted-foreground">{format(slice.value)}</p>
            </TooltipContent>
          </Tooltip>
        ))}
      </div>

      {/* The legend is also the direct labelling: past three series colour alone
          is not enough, and one of the slots sits below 3:1 on white. */}
      <ul className="flex flex-wrap gap-x-4 gap-y-1">
        {slices.map((slice) => (
          <li key={slice.id} className="flex items-center gap-1.5 text-xs">
            <span
              aria-hidden
              className="size-2.5 shrink-0 rounded-[2px]"
              style={{ backgroundColor: slice.color }}
            />
            <span className="text-foreground">{slice.name}</span>
            <span className="text-muted-foreground tabular-nums">
              {slice.percent.toFixed(1)}%
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
